import React, { useEffect, useState } from "react";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { cardStyle, mutedTextStyle, pageHeaderRegionStyle, pageHeaderStyle, pageScrollableContentStyle, pageShellStyle, pageTitleStyle, sectionTitleStyle } from "@presentation/common/pageLayout";

/**
 * The about page with information about:
 * 1. The software version and build number;
 * 2. Release notes;
 * 3. The list of third party libraries used.
 * License keys and deployment-level configuration live on the Settings page.
 * @returns Functional component
 */
const AboutPage: React.FC = () => {
  const [releaseNotes, setReleaseNotes] = useState("Loading release notes...");
  const [releaseNotesError, setReleaseNotesError] = useState("");

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const response = await fetch("release-notes.md", { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Failed to load release notes (${response.status})`);
        }
        const text = await response.text();
        const normalizedReleaseNotes = text.trim();
        setReleaseNotes(normalizedReleaseNotes || "No release notes available yet.");
        setReleaseNotesError("");
      } catch (error) {
        setReleaseNotes("No release notes available yet.");
        setReleaseNotesError(error instanceof Error ? error.message : String(error));
      }
    };

    fetchReleaseNotes();
  }, []);

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>About</h1>
        </div>
      </div>

      <div style={pageScrollableContentStyle}>
        <Card style={cardStyle}>
          <CardBody style={{ display: "grid", gap: 8 }}>
            <h2 style={sectionTitleStyle}>OmicsFlow v{runtimeConfig.OMICSFLOW_GENERAL_VERSION}</h2>
          </CardBody>
        </Card>

        <Card style={cardStyle}>
          <CardBody style={{ display: "grid", gap: 12 }}>
            <h2 style={sectionTitleStyle}>Release notes</h2>
            {releaseNotesError && (
              <div role="alert" style={{ color: "#b00020" }}>
                {releaseNotesError}
              </div>
            )}
            <pre
              style={{
                maxHeight: 320,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                border: "1px solid #d7dee8",
                borderRadius: 8,
                padding: 12,
                backgroundColor: "#f8fafc",
                margin: 0,
              }}
            >
              {releaseNotes}
            </pre>
          </CardBody>
        </Card>

        <Card style={cardStyle}>
          <CardBody style={{ display: "grid", gap: 8 }}>
            <h2 style={sectionTitleStyle}>Third party libraries</h2>
            <a style={mutedTextStyle} href="https://biognosys.com/content/uploads/2025/12/ThirdPartyDependences_OmicsFlow.pdf">
              https://biognosys.com/content/uploads/2025/12/ThirdPartyDependences_OmicsFlow.pdf
            </a>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default AboutPage;
